/**
 * Werte, Beschriftungen und Prüfregeln der Benutzerverwaltung und des Profils (Story 1.7).
 * Ohne Datenbank: Server (Commands, Routen) und Web-App (Formulare) prüfen mit denselben Zod-Schemas.
 */
import { z } from 'zod';
import { isOfficeRole, roles, type Role } from './actor.ts';

/** Sperre (`status`): `blocked` = Server lehnt ab, Anmeldung bei Supabase Auth gebannt (D4). */
export const userStatuses = ['active', 'blocked'] as const;
export type UserStatus = (typeof userStatuses)[number];

/** Arbeitsbereich: Schwerpunkt der Person, keine Grenze (FR-72, D8). */
export const workAreas = ['orders', 'warehouse', 'accounting', 'purchasing'] as const;
export type WorkArea = (typeof workAreas)[number];

/** Dichte der Arbeitslisten (DESIGN „Ruhig · Kompakt“). */
export const densities = ['calm', 'compact'] as const;
export type Density = (typeof densities)[number];

/**
 * Gespeicherte Hell/Dunkel-Wahl. `system` = keine eigene Wahl (Altwert aus der Zeit vor Design E · final und Wert
 * alter Clients); gilt seit Change 03.10.2026 Design E · final (Rudolf) als hell, nie mehr nach Windows.
 */
export const themePreferences = ['light', 'dark', 'system'] as const;
export type ThemePreference = (typeof themePreferences)[number];

/** Was die Oberfläche anbietet (EXPERIENCE §1): nur Hell · Dunkel, keine Option „Wie Windows“. */
export const themeChoices = ['light', 'dark'] as const;
export type ThemeChoice = (typeof themeChoices)[number];

/** Wirksame Ansicht: nur eine ausdrückliche Wahl „dunkel“ macht dunkel; alles andere (hell, `system`, keine) ist hell. */
export function effectiveTheme(pref: ThemePreference | null | undefined): ThemeChoice {
  return pref === 'dark' ? 'dark' : 'light';
}

/**
 * Seitenleiste am Desktop (Change 03.10.2026 Design E · final; Change 11.10.2026 Design S3): je Person gemerkt;
 * `collapsed` = schmale Symbolleiste (Standard für neue Personen), `expanded` = breite Leiste.
 */
export const sidebarStates = ['expanded', 'collapsed'] as const;
export type SidebarState = (typeof sidebarStates)[number];

/**
 * Spalte „Ergebnis, wenn verletzt“ der Karte „Ampel-Prüfungen beim Auftragseingang“ (Einstellungen › Verkauf › Aufträge,
 * Story 7.40): je Person ein- oder ausgeklappt; Start `expanded`.
 */
export const orderChecksResultStates = ['expanded', 'collapsed'] as const;
export type OrderChecksResultState = (typeof orderChecksResultStates)[number];

/**
 * Einklappbare Spalten je Tabelle (gemeinsamer Baustein „Spalte einklappen“, Story 7.41): Tabellen-Kennung → Zustand
 * (`expanded` | `collapsed`), je Person im Profilwert `columnStates`; fehlende Kennung = ausgeklappt. Neue Tabelle = neue
 * Kennung hier (mit Beschriftung für den Verlauf). Die Ergebnis-Spalte der Ampel-Prüfungen bleibt `orderChecksResult`.
 */
export const collapsibleColumns = ['shippingMethodsId', 'paymentMethodsId'] as const;
export type CollapsibleColumn = (typeof collapsibleColumns)[number];
export const columnStateValues = ['expanded', 'collapsed'] as const;
export type ColumnState = (typeof columnStateValues)[number];
/** Gespeicherte Zustände (nur genannte Tabellen; fehlend = ausgeklappt). */
export type ColumnStates = Partial<Record<CollapsibleColumn, ColumnState>>;

/** Zustand einer Tabelle; ohne gespeicherten Wert ausgeklappt. */
export function columnStateOf(states: ColumnStates | null | undefined, column: CollapsibleColumn): ColumnState {
  return states?.[column] ?? 'expanded';
}

/**
 * Erinnerungen vor Fälligkeit in der Glocke (Story 23.13): je Person an oder aus; Start `on`. Aus = der tägliche Lauf
 * lässt die Person aus (Aufgaben anderer Personen erinnern weiter).
 */
export const taskReminderStates = ['on', 'off'] as const;
export type TaskReminderState = (typeof taskReminderStates)[number];

/**
 * Schalter der Seite Berichte › Zahlungseingänge (Story 18.27) je Person: „Alle zahlen pünktlich“ (`punctual`),
 * „Unsichere dazunehmen“ (`uncertain`), „Offene Aufträge einrechnen“ (`orders`). `null` = Startwerte (aus, aus,
 * Einstellung „Offene Aufträge einrechnen“).
 */
export interface IncomingForecastViewPreference {
  punctual: boolean;
  uncertain: boolean;
  orders: boolean;
}

/** Satz für Nicht-Admins auf Berichte › Zahlungseingänge (Seite, Route und Command; eine Quelle, Story 18.27). */
export const INCOMING_FORECAST_FORBIDDEN_TEXT = 'Zahlungseingänge sieht nur ein Admin.';

/** Profilwerte, die `platform.updateMyPreferences` ändert (auch Inhalt von `platform.PreferencesChanged`). */
export const preferenceFields = ['density', 'theme', 'sidebar', 'reorderView', 'orderChecksResult', 'columnStates', 'taskReminders', 'incomingForecastView'] as const;
export type PreferenceField = (typeof preferenceFields)[number];

/** Rechte-Rollen für Auswahllisten (dieselben Werte wie `roles` aus actor.ts). */
export const userRoles = roles;
/** Rolle eines Benutzers (für die Web-App, die `actor.ts` nicht direkt liest). */
export type UserRole = Role;

/** Büro-Rolle (`user`, `admin`): nicht Packplatz und nicht Mitarbeiter (Story 22.11). */
export const isOfficeUserRole = isOfficeRole;

export const roleLabels: Record<Role, string> = { user: 'Benutzer', admin: 'Admin', packplatz: 'Packplatz', mitarbeiter: 'Mitarbeiter' };
/** Ein Satz je Rolle für die Rollen-Auswahl unter Einstellungen › Benutzer (Ticket 1.95). */
export const roleHints: Record<Role, string> = {
  user: 'Alles Fachliche – Aufträge, Kunden, Artikel. Keine Einstellungen.',
  admin: 'Alles, auch Einstellungen, Zugänge und Benutzer.',
  packplatz: 'Versandzentrum und eigene Arbeitszeit. Keine Preise, Umsätze oder Rechnungen.',
  mitarbeiter: 'Nur Stempeln, eigene Zeiten und eigener Urlaub. Keine Kunden, Aufträge oder Preise.',
};
export const workAreaLabels: Record<WorkArea, string> = {
  orders: 'Aufträge',
  warehouse: 'Lager',
  accounting: 'Buchhaltung',
  purchasing: 'Einkauf',
};
export const densityLabels: Record<Density, string> = { calm: 'Ruhig', compact: 'Kompakt' };
export const themeLabels: Record<ThemePreference, string> = { light: 'Hell', dark: 'Dunkel', system: 'ohne Wahl' };
export const sidebarLabels: Record<SidebarState, string> = { expanded: 'Breit', collapsed: 'Schmal' };
export const orderChecksResultLabels: Record<OrderChecksResultState, string> = { expanded: 'Ausgeklappt', collapsed: 'Eingeklappt' };
export const columnStateLabels: Record<ColumnState, string> = { expanded: 'Ausgeklappt', collapsed: 'Eingeklappt' };
export const taskReminderLabels: Record<TaskReminderState, string> = { on: 'An', off: 'Aus' };
/** Spalte und Tabelle für den Verlauf, z. B. „Spalte Shopware-ID (Versandarten)“. */
export const collapsibleColumnLabels: Record<CollapsibleColumn, string> = {
  shippingMethodsId: 'Spalte Shopware-ID (Versandarten)',
  paymentMethodsId: 'Spalte Shopware-ID (Zahlarten)',
};
/**
 * Nur für den Verlauf alter Einträge: „Farbe der Seitenleiste“ (Change 05.10.2026) gibt es seit Design S3 (Migration
 * 0199) nicht mehr; frühere Änderungen bleiben im Profil-Verlauf lesbar.
 */
export const legacySidebarColorLabels: Record<string, string> = { light: 'Hell', navy: 'Dunkelblau' };
export const userStatusLabels: Record<UserStatus, string> = { active: 'Aktiv', blocked: 'Gesperrt' };

/** Feste Sätze der Benutzerverwaltung (Ursache + nächster Schritt). */
export const USER_TEXTS = {
  blocked: 'Dein Zugang ist gesperrt. Bitte Rudolf ansprechen.',
  forbidden: 'Nur Admins dürfen Benutzer verwalten.',
  notFound: 'Diesen Benutzer gibt es nicht.',
  conflict: 'Jemand anderes hat diesen Benutzer inzwischen geändert. Sieh dir den neuen Stand an und speichere dann noch einmal.',
  emailTaken: 'Diese E-Mail-Adresse hat schon ein Benutzer im ERP.',
  emailInvalid: 'Bitte eine gültige E-Mail-Adresse eingeben.',
  nameMissing: 'Bitte einen Namen eingeben.',
  nameTooLong: 'Der Name darf höchstens 200 Zeichen haben.',
  roleMissing: 'Bitte eine Rolle wählen.',
  workAreaInvalid: 'Bitte einen Arbeitsbereich aus der Liste wählen.',
  lastAdmin: 'Es muss mindestens ein aktiver Admin bleiben. Mach zuerst eine andere Person zum Admin.',
  /** Story 28.3: der einzige aktive Admin will sich selbst eine andere Rolle geben (Sie-Form wie die Abnahme). */
  onlyAdmin: 'Sie sind der einzige Admin. Legen Sie zuerst einen zweiten Admin an.',
  /** Story 28.3: ein Admin will sich selbst die Admin-Rolle nehmen, obwohl es weitere gibt. */
  selfDemote: 'Sie können sich nicht selbst die Admin-Rolle nehmen. Das kann nur ein anderer Admin.',
  selfBlock: 'Du kannst dich nicht selbst sperren.',
  blockedNoLink: 'Gesperrte Benutzer bekommen keinen Einrichtungs-Link. Bitte zuerst entsperren.',
  versionMissing: 'Die gelesene Version fehlt. Bitte lade die Seite neu.',
  densityInvalid: 'Bitte „Ruhig“ oder „Kompakt“ wählen.',
  themeInvalid: 'Bitte „Hell“ oder „Dunkel“ wählen.',
  sidebarInvalid: 'Bitte „Breit“ oder „Schmal“ wählen.',
  orderChecksResultInvalid: 'Ergebnis-Spalte: bitte „Ausgeklappt“ oder „Eingeklappt“ wählen.',
  taskRemindersInvalid: 'Erinnerungen in der Glocke: bitte „An“ oder „Aus“ wählen.',
  incomingForecastViewInvalid: 'Schalter der Zahlungseingänge unvollständig. Bitte die Seite neu laden.',
  columnStatesInvalid: 'Spalte einklappen: unbekannte Tabelle oder Wert. Bitte „Ausgeklappt“ oder „Eingeklappt“ wählen und die Seite neu laden.',
  reorderSalesMonthsInvalid: 'Rückblick: bitte ganze Monate, mindestens 1.',
  reorderPlanningMonthsInvalid: 'Vorausplanung: bitte ganze Monate, ab 0.',
  reorderBasisMissing: 'Mindestens eine Grundlage: Verkäufe oder Mindestbestand.',
  reorderBasisInvalid: 'Grundlage: bitte an oder aus.',
  reorderViewInvalid: 'Die Startwerte für den Bestellvorschlag sind unvollständig. Bitte die Seite neu laden.',
} as const;

export const userEmailSchema = z.email({ error: USER_TEXTS.emailInvalid }).max(320, USER_TEXTS.emailInvalid);
export const userNameSchema = z
  .string({ error: USER_TEXTS.nameMissing })
  .trim()
  .min(1, USER_TEXTS.nameMissing)
  .max(200, USER_TEXTS.nameTooLong);
export const userRoleSchema = z.enum(roles, { error: USER_TEXTS.roleMissing });
export const workAreaSchema = z.enum(workAreas, { error: USER_TEXTS.workAreaInvalid }).nullable();
export const densitySchema = z.enum(densities, { error: USER_TEXTS.densityInvalid });
/** Schnittstelle: nimmt `system` weiter an (alte Clients, Tests); gilt als „keine eigene Wahl“ = hell. */
export const themePreferenceSchema = z.enum(themePreferences, { error: USER_TEXTS.themeInvalid });
/** Oberfläche (Profil, Knopf „Dunkles Design“): nur Hell · Dunkel. */
export const themeChoiceSchema = z.enum(themeChoices, { error: USER_TEXTS.themeInvalid });
export const sidebarSchema = z.enum(sidebarStates, { error: USER_TEXTS.sidebarInvalid });
export const orderChecksResultSchema = z.enum(orderChecksResultStates, { error: USER_TEXTS.orderChecksResultInvalid });
export const taskRemindersSchema = z.enum(taskReminderStates, { error: USER_TEXTS.taskRemindersInvalid });
/** Schalter der Zahlungseingänge (Story 18.27): alle drei an oder aus. */
export const incomingForecastViewSchema = z.strictObject(
  { punctual: z.boolean(), uncertain: z.boolean(), orders: z.boolean() },
  { error: USER_TEXTS.incomingForecastViewInvalid },
);
/** Für den Verlauf, z. B. „pünktlich an · Unsichere aus · Aufträge an“. */
export function describeIncomingForecastView(view: IncomingForecastViewPreference | null | undefined): string {
  if (!view) return 'Startwerte';
  const onOff = (b: boolean) => (b ? 'an' : 'aus');
  return `Pünktlich ${onOff(view.punctual)} · Unsichere ${onOff(view.uncertain)} · Aufträge ${onOff(view.orders)}`;
}
/** Teilweise Änderung der einklappbaren Spalten: nur bekannte Tabellen-Kennungen, nur `expanded` | `collapsed`. */
export const columnStatesSchema = z.partialRecord(
  z.enum(collapsibleColumns, { error: USER_TEXTS.columnStatesInvalid }),
  z.enum(columnStateValues, { error: USER_TEXTS.columnStatesInvalid }),
  { error: USER_TEXTS.columnStatesInvalid },
);
/**
 * Eigene Startwerte der Rechen-Leiste („Als meine Startwerte merken“); mindestens eine Grundlage bleibt an. Hier nur die
 * Form (Rückblick ganze Monate ab 1, Vorausplanung ab 0 wie die Einstellung der Firma); die wirksamen Grenzen (Einstellungen
 * des Einkaufs, erweitert um die Startwerte der Firma) prüft der Server vorher (AD-1: platform kennt purchasing nicht).
 */
export const reorderViewSchema = z
  .object(
    {
      salesMonths: z
        .number({ error: USER_TEXTS.reorderSalesMonthsInvalid })
        .int(USER_TEXTS.reorderSalesMonthsInvalid)
        .min(1, USER_TEXTS.reorderSalesMonthsInvalid),
      planningMonths: z
        .number({ error: USER_TEXTS.reorderPlanningMonthsInvalid })
        .int(USER_TEXTS.reorderPlanningMonthsInvalid)
        .min(0, USER_TEXTS.reorderPlanningMonthsInvalid),
      sales: z.boolean({ error: USER_TEXTS.reorderViewInvalid }),
      minimumStock: z.boolean({ error: USER_TEXTS.reorderViewInvalid }),
    },
    { error: USER_TEXTS.reorderViewInvalid },
  )
  .refine((v) => v.sales || v.minimumStock, { message: USER_TEXTS.reorderBasisMissing, path: ['sales'] });
export type ReorderView = z.infer<typeof reorderViewSchema>;

/** Kurzform für den Verlauf, z. B. „6 Monate Rückblick, 2 Monate voraus, Verkäufe und Mindestbestand“. */
export function describeReorderView(view: ReorderView | null | undefined): string {
  if (!view) return 'Startwerte der Firma';
  const basis = [view.sales ? 'Verkäufe' : null, view.minimumStock ? 'Mindestbestand' : null].filter((b) => b !== null).join(' und ');
  return `${view.salesMonths} Monate Rückblick, ${view.planningMonths} Monate voraus, ${basis}`;
}

export const expectedVersionSchema = z
  .number({ error: USER_TEXTS.versionMissing })
  .int(USER_TEXTS.versionMissing)
  .min(1, USER_TEXTS.versionMissing);

/** Eingabe „Benutzer anlegen“ (Formular und Schnittstelle). */
export const newUserSchema = z.object({
  email: userEmailSchema,
  displayName: userNameSchema,
  role: userRoleSchema,
  workArea: workAreaSchema.default(null),
});
export type NewUser = z.output<typeof newUserSchema>;
